import React, { useEffect, useState } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import type { SetupIntentResponse } from '@lds-efsp/shared-contracts';
import { useBrand } from '../../../app/config/brand';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import { Button } from '../../../shared/components/ui';

interface AddCardModalProps {
  stripePromise: Promise<Stripe | null>;
  onClose: () => void;
  onAdded: () => void;
}

// Inner form — must live inside <Elements> to use the Stripe hooks.
const CardSetupForm: React.FC<{ onClose: () => void; onAdded: () => void }> = ({ onClose, onAdded }) => {
  const brand = useBrand();
  const stripe = useStripe();
  const elements = useElements();
  const { post } = useApi();
  const [submitting, setSubmitting] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message ?? 'Could not save card. Please try again.');
      setSubmitting(false);
      return;
    }
    if (setupIntent?.status !== 'succeeded') {
      setError('Card setup did not complete. Please try again.');
      setSubmitting(false);
      return;
    }

    try {
      await post(`${API_BASE_URL}/api/payments/methods`, {
        setupIntentId: setupIntent.id,
        setAsDefault,
      });
      onAdded();
    } catch (err) {
      setError((err as Error).message || 'Failed to save card.');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />

      <label
        className="flex items-center gap-2 mt-4 text-sm cursor-pointer"
        style={{ color: brand.colors.text }}
      >
        <input
          type="checkbox"
          checked={setAsDefault}
          onChange={(e) => setSetAsDefault(e.target.checked)}
        />
        Set as default payment method
      </label>

      {error && (
        <div className="mt-3 text-sm" style={{ color: brand.colors.error }}>
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-6">
        <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !stripe}>
          {submitting ? 'Saving…' : 'Add card'}
        </Button>
      </div>
    </form>
  );
};

export const AddCardModal: React.FC<AddCardModalProps> = ({ stripePromise, onClose, onAdded }) => {
  const brand = useBrand();
  const { post } = useApi();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    post<SetupIntentResponse>(`${API_BASE_URL}/api/payments/setup-intent`)
      .then((res) => {
        if (!cancelled) setClientSecret(res.clientSecret);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message || 'Could not start card setup.');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: `${brand.colors.secondary}80` }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="rounded-xl p-6 w-full max-w-md"
        style={{ background: brand.colors.bgCard, boxShadow: `0 20px 50px ${brand.colors.secondary}4D` }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold" style={{ color: brand.colors.secondary }}>
            Add a card
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-2xl leading-none"
            style={{ color: brand.colors.textMuted }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="text-sm mb-3" style={{ color: brand.colors.error }}>
            {error}
          </div>
        )}

        {clientSecret ? (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: { theme: 'stripe', variables: { colorPrimary: brand.colors.primary } },
            }}
          >
            <CardSetupForm onClose={onClose} onAdded={onAdded} />
          </Elements>
        ) : (
          !error && (
            <div className="py-10 text-center text-sm" style={{ color: brand.colors.textLight }}>
              Loading secure card form…
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AddCardModal;
